/**
 * Redeem requests from the Player's shop that wait for the Player to approve or decline
 * (Kiosk_Backend documents/REDEEM_PLAYER_CONSENT.md).
 */
import { apiService } from '@/services/apiService';

export type RedeemApprovalStatus = 'pending' | 'approved' | 'declined' | 'expired' | 'cancelled' | 'voided' | 'failed';

export interface RedeemApprovalRequest {
  id: string;
  status: RedeemApprovalStatus;
  shop_name: string;
  credits: number;
  cash_paid: number;
  created_at: string | null;
  expires_at: string | null;
  requested_by: 'shop' | 'cashier';
}

export interface PendingRedeemRequests {
  requests: RedeemApprovalRequest[];
  pollAfterMs: number;
}

export type RedeemDecisionResult =
  | { ok: true; request: RedeemApprovalRequest }
  | { ok: false; code?: string; message: string };

export const DEFAULT_POLL_MS = 15000;
const MIN_POLL_MS = 3000;

const BASE = '/users/me/redeem-requests';

export async function fetchPendingRedeemRequests(): Promise<PendingRedeemRequests> {
  const res = await apiService.get<{ requests?: RedeemApprovalRequest[]; poll_after_ms?: number }>(`${BASE}/pending`, true, true);
  const poll = Number(res?.data?.poll_after_ms);
  return {
    requests: Array.isArray(res?.data?.requests) ? res.data!.requests! : [],
    pollAfterMs: Number.isFinite(poll) && poll >= MIN_POLL_MS ? poll : DEFAULT_POLL_MS,
  };
}

async function decide(requestId: string, action: 'approve' | 'decline'): Promise<RedeemDecisionResult> {
  try {
    const res = await apiService.post<{ request: RedeemApprovalRequest }>(
      `${BASE}/${encodeURIComponent(requestId)}/${action}`,
      {},
      true,
    );
    if (res?.success && res.data?.request) return { ok: true, request: res.data.request };
    return { ok: false, message: res?.message || 'Something went wrong. Please try again.' };
  } catch (error) {
    const e = error as { code?: string; message?: string };
    return { ok: false, code: e?.code, message: e?.message || 'Something went wrong. Please try again.' };
  }
}

export function approveRedeemRequest(requestId: string): Promise<RedeemDecisionResult> {
  return decide(requestId, 'approve');
}

export function declineRedeemRequest(requestId: string): Promise<RedeemDecisionResult> {
  return decide(requestId, 'decline');
}
